import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { apiFetch } from '../../../shared/lib/apiClient';
import { declareTourStep, useTour, useTourStep } from '../../../shared/lib/TourContext';
import VendorReviewBanner from './VendorReviewBanner';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt$ = (n) => {
  if (n == null) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
};

const fmtTime = (str) => {
  if (!str) return '';
  const d = new Date(str);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
};

const useCurrentTime = () => {
  const [time, setTime] = useState(() =>
    new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  );
  useEffect(() => {
    const interval = setInterval(() =>
      setTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }))
    , 60000);
    return () => clearInterval(interval);
  }, []);
  return time;
};

const STATUS_PILL = {
  unpaid:         { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'Unpaid' },
  paid:           { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Paid' },
  partially_paid: { bg: 'bg-blue-50',    text: 'text-blue-700',    label: 'Partial' },
  complete:       { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Complete' },
  extracting:     { bg: 'bg-amber-50',   text: 'text-amber-600',   label: 'Extracting' },
  received:       { bg: 'bg-zinc-100',   text: 'text-zinc-500',    label: 'Received' },
  failed:         { bg: 'bg-red-50',     text: 'text-red-500',     label: 'Failed' },
};

// ---------------------------------------------------------------------------
// SVG Icons
// ---------------------------------------------------------------------------
const IconClock = ({ className }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="8" cy="8" r="6.5" /><path d="M8 4.5V8l2.5 1.5" />
  </svg>
);
const IconInbox = ({ className }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M2 10l2.5-5.5A1 1 0 015.4 4h5.2a1 1 0 01.9.5L14 10v3a1 1 0 01-1 1H3a1 1 0 01-1-1v-3z" /><path d="M2 10h3.5a1 1 0 011 1v0a1 1 0 001 1h1a1 1 0 001-1v0a1 1 0 011-1H14" />
  </svg>
);
const IconAlert = ({ className }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M8 1.5L1.5 13h13L8 1.5z" /><path d="M8 6v3" /><circle cx="8" cy="11" r="0.5" fill="currentColor" />
  </svg>
);
const IconSync = ({ className }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M2 8a6 6 0 0110.2-4.2M14 2v4h-4" /><path d="M14 8a6 6 0 01-10.2 4.2M2 14v-4h4" />
  </svg>
);
// ---------------------------------------------------------------------------
// Today's Tray — single card with dividers
// ---------------------------------------------------------------------------
const TrayCell = ({ label, count, total, icon: Icon, danger, onClick }) => (
  <button
    onClick={onClick}
    className="flex-1 text-left px-3 py-3 sm:px-5 sm:py-4 hover:bg-zinc-50/60 active:bg-zinc-50 transition-colors group"
  >
    <div className="flex items-center gap-1.5 mb-1.5">
      <Icon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${danger ? 'text-red-400' : 'text-zinc-400'}`} />
      <p className={`text-[9px] sm:text-[10px] uppercase tracking-[0.2em] ${danger ? 'text-red-400' : 'text-zinc-400'}`}>{label}</p>
    </div>
    <p className={`text-xl sm:text-2xl font-light tabular-nums ${danger ? 'text-red-600' : 'text-zinc-900'}`}>{count}</p>
    {total != null && total > 0 ? (
      <p className={`text-[10px] sm:text-[11px] tabular-nums mt-0.5 ${danger ? 'text-red-400' : 'text-zinc-400'}`}>{fmt$(total)}</p>
    ) : (
      <p className="text-[10px] sm:text-[11px] text-transparent mt-0.5 select-none">&nbsp;</p>
    )}
  </button>
);

const TRAY_ITEMS = [
  { key: 'dueToday',     label: 'Due Today',     icon: IconClock, filter: 'due_today',     hasTotal: true },
  { key: 'arrivedToday', label: 'Arrived Today',  icon: IconInbox, filter: 'arrived_today' },
  { key: 'overdue',      label: 'Overdue',        icon: IconAlert, filter: 'overdue',       hasTotal: true, danger: true },
  { key: 'pendingSync',  label: 'Pending Sync',   icon: IconSync, filter: 'pending_sync' },
];

declareTourStep({
  id: 'overview-page',
  title: 'Overview',
  description: 'Your operating snapshot. See recent activity, spend, vendors, and processing status at a glance.',
  priority: 20,
  path: '/dashboard',
});

declareTourStep({
  id: 'metric-cards',
  title: 'Key Metrics',
  description: 'These cards summarize the current state of your payables workflow so you can spot volume and spend shifts quickly.',
  priority: 30,
  path: '/dashboard',
});

const Tray = ({ tray, onNavigate, loaded, tourRef }) => (
  <div
    ref={tourRef}
    className="bg-white border border-zinc-100 rounded-xl overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
    style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '400ms' }}
  >
    <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-zinc-100">
      {/* Top row on mobile (first 2), all 4 on desktop */}
      {TRAY_ITEMS.map((item, i) => (
        <div key={item.key} className={i >= 2 ? 'border-t border-zinc-100 lg:border-t-0' : ''}>
          <TrayCell
            label={item.label}
            count={tray[item.key]?.count || 0}
            total={item.hasTotal ? tray[item.key]?.total : undefined}
            icon={item.icon}
            danger={item.danger}
            onClick={() => onNavigate('bills', { filter: item.filter })}
          />
        </div>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Recent Activity Feed
// ---------------------------------------------------------------------------
const ActivityFeed = ({ items, loaded, onNavigate }) => {
  if (!items || items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <p className="text-sm text-zinc-400">No recent activity</p>
        <button onClick={() => onNavigate('documents')} className="mt-2 text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-800 transition-colors">
          Upload your first invoice
        </button>
      </div>
    );
  }

  const SOURCE_LABEL = { quickbooks: 'QuickBooks', xero: 'Xero' };

  return (
    <div className="divide-y divide-zinc-50">
      {items.map((item, i) => {
        const status = STATUS_PILL[item.payment_status] || STATUS_PILL[item.processing_status] || STATUS_PILL.received;
        const isProcessing = !['complete', 'failed'].includes(item.processing_status);
        const source = item.source_integration
          ? SOURCE_LABEL[item.source_integration] || item.source_integration
          : 'Upload';
        const ref = item.bill_number ? `#${item.bill_number}` : (item.file_name || null);
        return (
          <div
            key={item.id}
            className="flex items-center gap-2.5 sm:gap-3 px-4 sm:px-5 py-3 hover:bg-zinc-50/60 active:bg-zinc-50 transition-colors duration-100 cursor-pointer"
            onClick={() => onNavigate('bills')}
            style={{ opacity: loaded ? 1 : 0, transition: `opacity 0.5s ease ${600 + i * 50}ms` }}
          >
            {/* Vendor initial */}
            <div className="w-7 h-7 rounded-full bg-zinc-100 flex items-center justify-center shrink-0">
              <span className="text-[10px] font-medium text-zinc-500">
                {(item.vendor_name || '?').charAt(0).toUpperCase()}
              </span>
            </div>

            {/* Vendor + ref + source */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-zinc-800 truncate">{item.vendor_name || 'Unknown vendor'}</p>
                {ref && <span className="hidden sm:inline text-[10px] text-zinc-400 truncate max-w-[140px]">{ref}</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-zinc-400">{fmtTime(item.created_at)}</span>
                <span className="text-zinc-200">·</span>
                <span className="text-[11px] text-zinc-400">{source}</span>
                {item.due_date && (
                  <>
                    <span className="hidden sm:inline text-zinc-200">·</span>
                    <span className="hidden sm:inline text-[11px] text-zinc-400">Due {new Date(item.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </>
                )}
              </div>
            </div>

            {/* Amount */}
            <span className="text-sm font-medium text-zinc-800 tabular-nums shrink-0">
              {fmt$(item.total_amount)}
            </span>

            {/* Status pill */}
            <span className={`hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-lg font-medium shrink-0 ${status.bg} ${status.text}`}>
              {isProcessing && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
              {status.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Top Vendors with period tabs
// ---------------------------------------------------------------------------
const PERIODS = [
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'year', label: 'This Year' },
  { key: 'all', label: 'All Time' },
];

const TopVendors = ({ vendorsByPeriod, loaded, onNavigate }) => {
  const [period, setPeriod] = useState('month');
  const vendors = vendorsByPeriod?.[period] || [];
  const max = vendors[0]?.total || 1;
  const totalSpend = vendors.reduce((s, v) => s + v.total, 0);

  const hasAnyData = PERIODS.some((p) => (vendorsByPeriod?.[p.key] || []).length > 0);
  if (!hasAnyData) return null;

  return (
    <div
      className="bg-white border border-zinc-100 rounded-xl overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '900ms' }}
    >
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Top Vendors</p>
          <button onClick={() => onNavigate('vendors')} className="text-xs text-zinc-400 hover:text-zinc-900 transition-colors">
            View all →
          </button>
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                period === p.key
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {vendors.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-zinc-300">No bills in this period</p>
        </div>
      ) : (
        <>
          <div className="divide-y divide-zinc-50">
            {vendors.map((v, i) => {
              const pct = totalSpend > 0 ? ((v.total / totalSpend) * 100).toFixed(1) : '0';
              return (
                <div
                  key={v.vendor_id}
                  className="px-4 sm:px-5 py-3 flex items-center gap-3"
                  style={{ opacity: loaded ? 1 : 0, transition: `opacity 0.5s ease ${950 + i * 60}ms` }}
                >
                  <div className="w-7 h-7 rounded-full bg-zinc-100 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-medium text-zinc-500">
                      {(v.vendor_name || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-sm text-zinc-800 truncate">{v.vendor_name}</p>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm font-medium text-zinc-800 tabular-nums">{fmt$(v.total)}</span>
                        <span className="text-[10px] text-zinc-400 tabular-nums">{pct}%</span>
                      </div>
                    </div>
                    <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-zinc-900 rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${Math.max(4, (v.total / max) * 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-1">{v.count} bill{v.count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-4 sm:px-5 py-3 border-t border-zinc-100 flex items-center justify-between">
            <span className="text-[10px] text-zinc-400">Total</span>
            <span className="text-sm font-medium text-zinc-800 tabular-nums">{fmt$(totalSpend)}</span>
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Total Spend Chart
// ---------------------------------------------------------------------------
const CHART_RANGES = [
  { key: '1m', label: '1M', months: 1 },
  { key: '3m', label: '3M', months: 3 },
  { key: '6m', label: '6M', months: 6 },
  { key: '1y', label: '1Y', months: 12 },
  { key: 'all', label: 'All', months: 0 },
];

const SpendChart = ({ spendOverTime, loaded, embedded, heroLabel, heroChange }) => {
  const [range, setRange] = useState('all');
  const [mode, setMode] = useState('bar'); // 'line' | 'bar' — bar default, more readable
  const [hover, setHover] = useState(null);

  if (!spendOverTime || spendOverTime.length === 0) return null;

  const rangeConfig = CHART_RANGES.find((r) => r.key === range);
  const months = rangeConfig?.months
    ? spendOverTime.slice(-rangeConfig.months)
    : spendOverTime;

  if (months.length === 0) return null;

  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmtMonth = (str) => {
    const [y, m] = str.split('-');
    return `${MONTH_NAMES[parseInt(m, 10) - 1]}'${y.slice(2)}`;
  };
  const fmtMonthShort = (str) => {
    const m = str.split('-')[1];
    return MONTH_NAMES[parseInt(m, 10) - 1];
  };

  const values = months.map((m) => m.total);
  const totalInRange = values.reduce((s, v) => s + v, 0);
  const rawMax = Math.max(...values) || 1;
  const rawMin = Math.min(...values);
  // Dynamic floor: if the spike is >3x the median, compress the scale
  // so low months still show meaningful variation
  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const hasSpike = rawMax > median * 3 && median > 0;
  const floor = hasSpike ? Math.max(0, rawMin * 0.6) : 0;
  const max = rawMax - floor || 1;

  // Month-over-month change
  let changeLabel = null;
  if (values.length >= 2) {
    const prev = values[values.length - 2];
    const curr = values[values.length - 1];
    if (prev > 0) {
      const pct = ((curr - prev) / prev) * 100;
      const sign = pct >= 0 ? '+' : '';
      changeLabel = { text: `${sign}${pct.toFixed(1)}% vs prior month`, up: pct > 0 };
    }
  }

  // Hover data
  const hoverMonth = hover != null ? fmtMonth(months[hover].month) : null;
  const hoverAmount = hover != null ? fmt$(values[hover]) : null;

  const controls = (
    <div className={`flex items-end justify-between ${embedded ? 'px-6 pt-5 pb-2' : 'px-4 sm:px-5 pt-4 sm:pt-5 pb-2'}`}>
      {embedded ? (
        /* Hero header — title, big number, change badge on left; controls on right */
        <div>
          <p className="text-sm text-zinc-500 font-medium">Total Spend</p>
          <div className="flex items-baseline gap-3 mt-1">
            <p className="text-[32px] font-semibold text-zinc-900 tracking-tight tabular-nums leading-none">
              {hover != null ? hoverAmount : heroLabel}
            </p>
            {hover != null && hoverMonth ? (
              <span className="text-sm text-zinc-400">{hoverMonth}</span>
            ) : heroChange ? (
              <span className={`inline-flex items-center gap-1 text-sm font-medium ${heroChange.up ? 'text-red-500' : 'text-emerald-600'}`}>
                {heroChange.up ? '↑' : '↓'} {heroChange.amount} ({heroChange.pct}%)
              </span>
            ) : null}
          </div>
          {changeLabel && hover == null && (
            <p className="text-[11px] mt-1">
              <span className={changeLabel.up ? 'text-red-400' : 'text-emerald-500'}>{changeLabel.text}</span>
            </p>
          )}
        </div>
      ) : (
        /* Standalone — full header */
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Total Spend</p>
            {hoverMonth && (
              <p className="text-[10px] text-zinc-400">
                <span className="text-zinc-300">·</span> {hoverMonth}
              </p>
            )}
          </div>
          <p className="text-xl sm:text-2xl font-light text-zinc-900 tabular-nums mt-0.5">
            {hover != null ? hoverAmount : fmt$(totalInRange)}
          </p>
          <p className={`text-[11px] mt-0.5 h-4 ${changeLabel ? '' : 'invisible'}`}>
            {changeLabel && hover == null ? (
              <span className={changeLabel.up ? 'text-red-400' : 'text-emerald-500'}>{changeLabel.text}</span>
            ) : (
              <span className="text-transparent">&nbsp;</span>
            )}
          </p>
        </div>
      )}
      <div className="flex items-center gap-2 shrink-0">
        <div className="flex bg-zinc-100 rounded p-0.5">
          <button
            onClick={() => setMode('line')}
            className={`px-1.5 py-1 rounded transition-colors ${mode === 'line' ? 'bg-white shadow-sm text-zinc-700' : 'text-zinc-400'}`}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 12l4-5 3 3 5-7" /></svg>
          </button>
          <button
            onClick={() => setMode('bar')}
            className={`px-1.5 py-1 rounded transition-colors ${mode === 'bar' ? 'bg-white shadow-sm text-zinc-700' : 'text-zinc-400'}`}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 14V8M7 14V4M11 14V6M15 14V2" /></svg>
          </button>
        </div>
        <div className="flex gap-0.5">
          {CHART_RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                range === r.key
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const chartBody = (
    <div
      className={`relative ${embedded ? 'px-6 pb-4' : 'px-4 sm:px-5 pb-3'}`}
      onMouseLeave={() => setHover(null)}
    >
      {mode === 'bar' && (
        <div className="flex items-end gap-[3px] sm:gap-1" style={{ height: embedded ? 64 : 80 }}>
          {values.map((v, i) => {
            const pct = max > 0 ? ((v - floor) / max) * 100 : 0;
            const isActive = hover === i;
            const isDimmed = hover != null && !isActive;
            return (
              <div
                key={i}
                className="flex-1 flex flex-col items-center cursor-pointer group"
                style={{ height: '100%' }}
                onMouseEnter={() => setHover(i)}
                onTouchStart={() => setHover(i)}
              >
                <div className="flex-1 w-full flex items-end">
                  <div
                    className="w-full rounded-t transition-all duration-200"
                    style={{
                      height: `${Math.max(2, pct)}%`,
                      backgroundColor: isActive ? '#18181b' : '#27272a',
                      opacity: isDimmed ? 0.25 : 1,
                    }}
                  />
                </div>
                <span className={`text-[8px] sm:text-[9px] mt-1.5 tabular-nums transition-colors duration-150 ${
                  isActive ? 'text-zinc-900 font-semibold' : 'text-zinc-300'
                }`}>
                  {fmtMonthShort(months[i].month)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {mode === 'line' && (() => {
        const W = 600, H = embedded ? 64 : 90, PAD = { t: 8, b: 18, l: 20, r: 20 };
        const iW = W - PAD.l - PAD.r;
        const iH = H - PAD.t - PAD.b;

        const pts = values.map((v, i) => ({
          x: PAD.l + (values.length === 1 ? iW / 2 : (i / (values.length - 1)) * iW),
          y: PAD.t + (1 - (v - floor) / max) * iH,
        }));

        const linePath = pts.reduce((acc, p, i) => {
          if (i === 0) return `M ${p.x},${p.y}`;
          const prev = pts[i - 1];
          const cpx = (prev.x + p.x) / 2;
          return `${acc} C ${cpx},${prev.y} ${cpx},${p.y} ${p.x},${p.y}`;
        }, '');

        const areaPath = `${linePath} L ${pts[pts.length - 1].x},${PAD.t + iH} L ${pts[0].x},${PAD.t + iH} Z`;

        return (
          <svg className="w-full overflow-visible" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="spendGrad2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#18181b" stopOpacity="0.06" />
                <stop offset="100%" stopColor="#18181b" stopOpacity="0" />
              </linearGradient>
            </defs>

            <path d={areaPath} fill="url(#spendGrad2)" />
            <path d={linePath} fill="none" stroke="#18181b" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />

            {pts.map((p, i) => (
              <circle
                key={i} cx={p.x} cy={p.y}
                r={hover === i ? 4 : 2.5}
                fill={hover === i ? '#18181b' : 'white'}
                stroke="#18181b" strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
                style={{ transition: 'r 0.15s, fill 0.15s' }}
              />
            ))}

            {hover != null && pts[hover] && (
              <line x1={pts[hover].x} x2={pts[hover].x} y1={PAD.t} y2={PAD.t + iH}
                stroke="#18181b" strokeWidth="0.5" strokeDasharray="3,3" opacity="0.2"
                vectorEffect="non-scaling-stroke" />
            )}

            {months.map((m, i) => (
              <text key={i} x={pts[i].x} y={H - 2} textAnchor="middle"
                fontSize="9" fill={hover === i ? '#18181b' : '#d4d4d8'}
                fontWeight={hover === i ? '600' : '400'}>
                {fmtMonthShort(m.month)}
              </text>
            ))}

            {pts.map((p, i) => {
              const sliceW = iW / Math.max(values.length, 1);
              return (
                <rect key={`h${i}`} x={p.x - sliceW / 2} y={0} width={sliceW} height={H}
                  fill="transparent" style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHover(i)} onTouchStart={() => setHover(i)} />
              );
            })}
          </svg>
        );
      })()}
    </div>
  );

  if (embedded) {
    return <>{controls}{chartBody}</>;
  }

  return (
    <div
      className="bg-white border border-zinc-100 rounded-xl overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '850ms' }}
    >
      {controls}
      {chartBody}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Spend by Domain — expandable vertical table
// ---------------------------------------------------------------------------
// Deterministic color palette — hashes any domain name to a consistent hue
const DOMAIN_PALETTE = [
  { bar: '#ef4444', bg: '#fef2f2', text: '#dc2626' },
  { bar: '#3b82f6', bg: '#eff6ff', text: '#2563eb' },
  { bar: '#8b5cf6', bg: '#f5f3ff', text: '#7c3aed' },
  { bar: '#0ea5e9', bg: '#f0f9ff', text: '#0284c7' },
  { bar: '#f59e0b', bg: '#fffbeb', text: '#d97706' },
  { bar: '#06b6d4', bg: '#ecfeff', text: '#0891b2' },
  { bar: '#10b981', bg: '#ecfdf5', text: '#059669' },
  { bar: '#6366f1', bg: '#eef2ff', text: '#4f46e5' },
  { bar: '#ec4899', bg: '#fdf2f8', text: '#db2777' },
  { bar: '#f97316', bg: '#fff7ed', text: '#ea580c' },
];
const _domainColorCache = {};
const getDomainHex = (name) => {
  if (_domainColorCache[name]) return _domainColorCache[name];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  const color = DOMAIN_PALETTE[Math.abs(hash) % DOMAIN_PALETTE.length];
  _domainColorCache[name] = color;
  return color;
};

const DomainRow = ({ domain, totalSpend, defaultOpen }) => {
  const [hoveredCat, setHoveredCat] = useState(null);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const pct = totalSpend > 0 ? ((domain.total / totalSpend) * 100) : 0;
  const cats = domain.categories || [];
  const hex = getDomainHex(domain.name);

  return (
    <div>
      <button
        onClick={() => cats.length > 0 && setIsOpen(!isOpen)}
        className={`w-full text-left px-4 sm:px-5 py-3.5 transition-colors ${cats.length > 0 ? 'hover:bg-zinc-50/60 cursor-pointer' : 'cursor-default'}`}
      >
        {/* Top line: name + amount */}
        <div className="flex items-baseline justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-medium" style={{ color: hex.text }}>{domain.name}</span>
            {cats.length > 0 && (
              <svg width="8" height="8" viewBox="0 0 8 8" className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} style={{ color: hex.bar, opacity: 0.5 }}>
                <path d="M2 1L6 4L2 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-zinc-800 tabular-nums">{fmt$(domain.total)}</span>
            <span className="text-[10px] text-zinc-400 tabular-nums w-10 text-right">{pct.toFixed(1)}%</span>
          </div>
        </div>

        {/* Segmented bar — each category is a segment */}
        {cats.length > 1 ? (
          <div className="flex h-1.5 rounded-full overflow-hidden gap-px">
            {cats.map((cat) => {
              const segPct = domain.total > 0 ? (cat.total / domain.total) * 100 : 0;
              const isHovered = hoveredCat === cat.name;
              return (
                <div
                  key={cat.name}
                  className="h-full rounded-full transition-opacity duration-150"
                  style={{
                    width: `${Math.max(3, segPct)}%`,
                    backgroundColor: hex.bar,
                    opacity: hoveredCat && !isHovered ? 0.25 : (isHovered ? 1 : 0.7),
                  }}
                  onMouseEnter={() => setHoveredCat(cat.name)}
                  onMouseLeave={() => setHoveredCat(null)}
                />
              );
            })}
          </div>
        ) : (
          <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(4, pct)}%`, backgroundColor: hex.bar, opacity: 0.7 }} />
          </div>
        )}
      </button>

      {/* Expanded categories — pill chips */}
      {isOpen && cats.length > 0 && (
        <div className="px-4 sm:px-5 pb-3 -mt-1">
          <div className="flex flex-wrap gap-1.5">
            {cats.map((cat) => {
              const isHovered = hoveredCat === cat.name;
              return (
                <div
                  key={cat.name}
                  className="px-2.5 py-1 rounded-md text-[11px] transition-all duration-150 cursor-default"
                  style={{
                    backgroundColor: isHovered ? hex.bg : '#f9fafb',
                    color: isHovered ? hex.text : '#71717a',
                    borderBottom: isHovered ? `1.5px solid ${hex.bar}` : '1.5px solid transparent',
                  }}
                  onMouseEnter={() => setHoveredCat(cat.name)}
                  onMouseLeave={() => setHoveredCat(null)}
                >
                  <span className="font-medium">{cat.name}</span>
                  <span className="ml-1.5 opacity-60 tabular-nums">{fmt$(cat.total)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const SpendByCategory = ({ categoryByPeriod, loaded }) => {
  const [period, setPeriod] = useState('all');
  const domains = categoryByPeriod?.[period] || [];
  const totalSpend = domains.reduce((s, c) => s + c.total, 0);

  const hasAnyData = PERIODS.some((p) => (categoryByPeriod?.[p.key] || []).length > 0);
  if (!hasAnyData) return null;

  return (
    <div
      className="bg-white border border-zinc-100 rounded-xl overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '950ms' }}
    >
      <div className="px-4 sm:px-5 py-3 border-b border-zinc-100">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Spend by Domain</p>
          <span className="text-sm font-medium text-zinc-800 tabular-nums">{fmt$(totalSpend)}</span>
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                period === p.key
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {domains.length === 0 ? (
        <div className="px-5 py-5 text-center">
          <p className="text-sm text-zinc-300">No domain data for this period</p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-100/60">
          {domains.map((domain, di) => (
            <DomainRow key={domain.name} domain={domain} totalSpend={totalSpend} defaultOpen={di === 0} />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Upcoming Due (next 7 days)
// ---------------------------------------------------------------------------
const fmtDueDate = (str) => {
  if (!str) return '';
  const d = new Date(str + 'T00:00:00');
  const today = new Date();
  today.setHours(0,0,0,0);
  const diff = Math.ceil((d - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const UpcomingDue = ({ bills, loaded, onNavigate }) => {
  if (!bills || bills.length === 0) return null;

  return (
    <div
      className="bg-white border border-zinc-100 rounded-xl overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '950ms' }}
    >
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Due This Week</p>
        <button onClick={() => onNavigate('bills', { filter: 'due_today' })} className="text-xs text-zinc-400 hover:text-zinc-900 transition-colors">
          View all →
        </button>
      </div>
      <div className="divide-y divide-zinc-50">
        {bills.map((b, i) => {
          const d = new Date(b.due_date + 'T00:00:00');
          const today = new Date();
          today.setHours(0,0,0,0);
          const diff = Math.ceil((d - today) / 86400000);
          const isToday = diff === 0;
          const isTomorrow = diff === 1;
          return (
            <div
              key={b.id}
              className="px-4 sm:px-5 py-3 flex items-center gap-3 hover:bg-zinc-50/60 transition-colors cursor-pointer"
              onClick={() => onNavigate('bills')}
              style={{ opacity: loaded ? 1 : 0, transition: `opacity 0.5s ease ${1000 + i * 60}ms` }}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isToday ? 'bg-amber-100' : isTomorrow ? 'bg-amber-50' : 'bg-zinc-100'}`}>
                <IconClock className={`w-3.5 h-3.5 ${isToday ? 'text-amber-600' : isTomorrow ? 'text-amber-500' : 'text-zinc-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-800 truncate">{b.vendor_name || 'Unknown vendor'}</p>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[11px] font-medium ${isToday ? 'text-amber-600' : isTomorrow ? 'text-amber-500' : 'text-zinc-400'}`}>
                    {fmtDueDate(b.due_date)}
                  </span>
                  {b.bill_number && (
                    <>
                      <span className="text-zinc-200">·</span>
                      <span className="text-[11px] text-zinc-400">#{b.bill_number}</span>
                    </>
                  )}
                </div>
              </div>
              <span className="text-sm font-medium text-zinc-800 tabular-nums shrink-0">
                {fmt$(b.balance_due ?? b.total_amount)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------
const Skeleton = () => (
  <div className="max-w-[1400px] mx-auto space-y-4 sm:space-y-6 px-4 sm:px-0">
    <div className="animate-pulse">
      <div className="h-7 w-56 bg-zinc-100 rounded mb-2" />
      <div className="h-4 w-40 bg-zinc-50 rounded" />
    </div>
    <div className="bg-white border border-zinc-100 rounded-xl overflow-hidden animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-zinc-100">
        {[0,1,2,3].map((i) => (
          <div key={i} className={`px-3 py-3 sm:px-5 sm:py-4 ${i >= 2 ? 'border-t border-zinc-100 lg:border-t-0' : ''}`}>
            <div className="h-2.5 w-16 bg-zinc-100 rounded mb-3" />
            <div className="h-6 w-10 bg-zinc-100 rounded mb-1.5" />
            <div className="h-2 w-14 bg-zinc-50 rounded" />
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white border border-zinc-100 rounded-xl animate-pulse h-48 sm:h-64" />
  </div>
);

// ---------------------------------------------------------------------------
// Main OverviewView
// ---------------------------------------------------------------------------
const OverviewView = ({ loaded, onNavigate }) => {
  const { user, profile, activeCompany } = useAuth();
  const { start: startTour } = useTour();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout]   = useState(() => localStorage.getItem('arbcor_overview_layout') || 'split');
  const currentTime = useCurrentTime();

  const firstName = profile?.full_name?.split(' ')[0] || user?.name?.split(' ')[0] || 'there';

  const toggleLayout = () => {
    const next = layout === 'split' ? 'stacked' : 'split';
    setLayout(next);
    localStorage.setItem('arbcor_overview_layout', next);
  };

  const overviewRef = useTourStep('overview-page');
  const trayRef = useTourStep('metric-cards');

  const load = useCallback(async () => {
    if (!activeCompany?.companyId) return;
    try {
      const res = await apiFetch('/api/bills/overview', { companyId: activeCompany.companyId });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch { /* silent */ } finally { setLoading(false); }
  }, [activeCompany?.companyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Skeleton />;

  const tray = data?.tray || {};
  const queue = data?.actionQueue || {};
  const recent = data?.recentActivity || [];
  const topVendorsByPeriod = data?.topVendorsByPeriod || {};
  const categoryByPeriod = data?.categoryByPeriod || {};
  const spendOverTime = data?.spendOverTime || [];
  const upcomingDue = data?.upcomingDue || [];

  // Compute hero metric — total outstanding (unpaid) spend
  const totalOutstanding = (data?.tray?.dueToday?.total || 0) + (data?.tray?.overdue?.total || 0);
  const totalBills = recent.length;

  // Spend change
  const spendData = spendOverTime || [];
  let spendChange = null;
  if (spendData.length >= 2) {
    const curr = spendData[spendData.length - 1]?.total || 0;
    const prev = spendData[spendData.length - 2]?.total || 0;
    if (prev > 0) spendChange = { pct: ((curr - prev) / prev * 100).toFixed(1), up: curr > prev, amount: fmt$(Math.abs(curr - prev)) };
  }

  return (
    <div ref={overviewRef} className="max-w-[1400px] mx-auto">

      {/* ── Greeting ── */}
      <div
        className="flex items-center justify-between mb-6"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(8px)', transition: 'all 0.5s cubic-bezier(0.16,1,0.3,1) 100ms' }}
      >
        <h1 className="text-[22px] font-semibold text-zinc-900 tracking-[-0.01em]">Welcome back, {firstName}!</h1>
        <button
          onClick={startTour}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-zinc-400 hover:text-zinc-600 border border-zinc-200 hover:border-zinc-300 rounded-lg transition-colors"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="8" r="6.5" />
            <path d="M6.5 6a1.5 1.5 0 0 1 3 0c0 1-1.5 1-1.5 2.5M8 11.5v0" />
          </svg>
          Tour
        </button>
      </div>

      {/* -- Vendor Review Banner -- */}
      <VendorReviewBanner loaded={loaded} />

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6 mt-4">

        {/* ═══ LEFT COLUMN ═══ */}
        <div className="space-y-6 min-w-0">

          {/* Hero card — Total Spend with chart */}
          <div
            className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] overflow-hidden"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 200ms' }}
          >
            <SpendChart spendOverTime={spendOverTime} loaded={loaded} embedded
              heroLabel={fmt$(spendData.reduce((s, m) => s + (m.total || 0), 0))}
              heroChange={spendChange}
            />
          </div>

          {/* Spending this month — metric strip */}
          <div
            ref={trayRef}
            className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] overflow-hidden"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 300ms' }}
          >
            <div className="grid grid-cols-2 lg:grid-cols-4">
              {TRAY_ITEMS.map((item, i) => (
                <div key={item.key} className={`${i > 0 ? 'border-l border-zinc-100' : ''} ${i >= 2 ? 'border-t border-zinc-100 lg:border-t-0' : ''}`}>
                  <TrayCell
                    label={item.label}
                    count={tray[item.key]?.count || 0}
                    total={item.hasTotal ? tray[item.key]?.total : undefined}
                    icon={item.icon}
                    danger={item.danger}
                    onClick={() => onNavigate('bills', { filter: item.filter })}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Spend by Domain */}
          <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 400ms' }}>
            <SpendByCategory categoryByPeriod={categoryByPeriod} loaded={loaded} />
          </div>

          {/* Top Vendors */}
          <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 500ms' }}>
            <TopVendors vendorsByPeriod={topVendorsByPeriod} loaded={loaded} onNavigate={onNavigate} />
          </div>
        </div>

        {/* ═══ RIGHT COLUMN ═══ */}
        <div className="space-y-6">

          {/* Upcoming Due */}
          <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 250ms' }}>
            <UpcomingDue bills={upcomingDue} loaded={loaded} onNavigate={onNavigate} />
          </div>

          {/* Transactions / Recent Activity */}
          <div
            className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] overflow-hidden"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 350ms' }}
          >
            <div className="px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-zinc-900">Transactions</p>
                <p className="text-xs text-zinc-400 mt-0.5">Most recent</p>
              </div>
              <button onClick={() => onNavigate('bills')} className="text-xs text-zinc-400 hover:text-zinc-900 transition-colors font-medium">
                All transactions →
              </button>
            </div>
            <ActivityFeed items={recent} loaded={loaded} onNavigate={onNavigate} />
          </div>

          {/* Quick stats card */}
          <div
            className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] p-6"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 450ms' }}
          >
            <p className="text-sm font-semibold text-zinc-900 mb-4">Quick Stats</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Outstanding</span>
                <span className="text-sm font-semibold text-zinc-900 tabular-nums">{fmt$(totalOutstanding)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Overdue</span>
                <span className={`text-sm font-semibold tabular-nums ${(tray.overdue?.count || 0) > 0 ? 'text-red-600' : 'text-zinc-900'}`}>
                  {tray.overdue?.count || 0} bill{(tray.overdue?.count || 0) !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Vendors</span>
                <span className="text-sm font-semibold text-zinc-900 tabular-nums">{Object.keys(topVendorsByPeriod?.all || topVendorsByPeriod?.month || {}).length || '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Due Today</span>
                <span className={`text-sm font-semibold tabular-nums ${(tray.dueToday?.count || 0) > 0 ? 'text-amber-600' : 'text-zinc-900'}`}>
                  {fmt$(tray.dueToday?.total || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewView;
